import { useMemo, useState } from 'react'

import { useMutation, useQuery } from 'react-query'

import { useModifyLicenseContext } from '@/modules/licenses/components/modify-license-context.tsx'
import { upgradeService } from '@/modules/licenses/service/upgrade-service.ts'
import { handleError } from '@/utils/error'

import type { BillingPlan, PurchaseContext } from '../types'

export const useUpgradeLicenses = (
  creatorId: number,
  selectedPlan: BillingPlan | undefined,
  licenseMode: string,
  open: boolean,
) => {
  const [planPriceId, setPlanPriceId] = useState<string>()

  const { plans, licenses } = useModifyLicenseContext()
  const {
    data: upgradePreview,
    isFetching: isFetchingPreview,
    error: upgradePreviewError,
  } = useQuery(
    ['purchase', selectedPlan?.name, creatorId, licenseMode, open],
    async () => {
      const planPriceId =
        `${selectedPlan!.name}_${licenseMode}_price`.toLowerCase()

      setPlanPriceId(planPriceId)

      return await upgradeService.getUpgrade({
        creatorId,
        planPrice: planPriceId,
      })
    },
    {
      enabled: open && !!creatorId && !!selectedPlan,
      onError: (error) => handleError(error, 'Failed to get upgrade preview'),
    },
  )

  const upgradeMutation = useMutation((context: PurchaseContext) => {
    return upgradeService.postUpgrade(context)
  })

  return useMemo(
    () => ({
      licenses,
      plans,
      upgradePreview,
      isFetchingPreview,
      upgradeMutation,
      planPriceId,
      upgradePreviewError,
    }),
    [
      licenses,
      plans,
      upgradePreview,
      isFetchingPreview,
      upgradeMutation,
      planPriceId,
      upgradePreviewError,
    ],
  )
}
